import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLoading } from "@/contexts/LoadingContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  fetchGroups,
  fetchLicenses,
  fetchOrgUser,
  updateUser,
} from "../services/organizationServices";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";

import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

interface EditUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser: SubUser | null;
  updateOnEdit: () => void | Promise<void>;
}

interface License {
  id: string;
  name: string;
  product_id: string;
  quantity: number;
  show_group_ids_dropdown?: boolean;
}

interface CreationDetails {
  paymentDetails: PaymentDetails;
}

interface PaymentDetails {
  provider: string;
  productId: string;
  subscriptionId: string;
}

interface SubscriptionDetails {
  currency?: string;
  name?: string;
  object?: string;
  price?: number;
  price_id?: string;
  stripe_product_id?: string;
  quantity?: number;
  show_group_ids_dropdown?: boolean;
}

interface SubUser {
  _id: string;
  email: string;
  status: number;
  creationDetails: CreationDetails;
  fullName: string;
  pgCustomerId: string;
  subscriptionDetails: SubscriptionDetails;
  product_id?: string;
  group_ids?: number[];
}

export interface AppGroups {
  app_name: string;
  groups: Group[];
}
export interface Group {
  group_id: number;
  group_name: string;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  isOpen,
  onClose,
  selectedUser,
  updateOnEdit,
}) => {
  const [userDetails, setUserDetails] = useState({
    fullName: "",
    email: "",
    subscriptionDetails: {
      name: "",
      currency: "",
      object: "",
      price: 0,
      price_id: "",
      product_id: "",
      quantity: 0,
    } as SubscriptionDetails,
  });

  const [selectedApps, setSelectedApps] = useState<string[]>([]);

  const { setLoading } = useLoading();

  const [licensesList, setLicensesList] = useState<License[]>([]);
  const [selectedLicenseDetails, setSelectedLicenseDetails] =
    useState<SubscriptionDetails | null>(null);
  const [subUsers, setSubUsers] = useState<SubUser[]>([]);
  const [productId, setProductId] = useState("");
  const [productid, setProductid] = useState("");

  const [appGroups, setAppGroups] = useState<{ [key: string]: Group[] }>({});
  const [selectedGroups, setSelectedGroups] = useState<
    Record<string, string | undefined>
  >({});

  // Prefill form when selectedUser changes
  console.log("line 104", selectedUser);
  useEffect(() => {
    if (selectedUser) {
      setUserDetails({
        fullName: selectedUser.fullName || "",
        email: selectedUser.email || "",
        subscriptionDetails: selectedUser.subscriptionDetails || {
          name: "",
          currency: "",
          object: "",
          price: 0,
          price_id: "",
          product_id: "",
          quantity: 0,
        },
      });

      console.log("line 122", productId);

      setSelectedLicenseDetails(selectedUser.subscriptionDetails || null);
      const productid =
        selectedUser?.subscriptionDetails?.stripe_product_id ?? "";

      setProductid(productid);

      if (selectedUser.group_ids && Object.keys(appGroups).length > 0) {
        const preSelectedApps: string[] = [];
        const preSelectedGroups: Record<string, string> = {};

        Object.entries(appGroups).forEach(([appName, groups]) => {
          for (const group of groups) {
            if (selectedUser.group_ids?.includes(group.group_id)) {
              preSelectedApps.push(appName);
              preSelectedGroups[appName] = String(group.group_id);
              break; // Assume one group per app
            }
          }
        });

        setSelectedApps(preSelectedApps);
        setSelectedGroups(preSelectedGroups);
      }
    }
  }, [selectedUser, isOpen, appGroups]);
  useEffect(() => {
    if (selectedUser) {
      const pid = selectedUser.subscriptionDetails?.stripe_product_id ?? "";
      setProductId(pid);
    }
  }, [selectedUser]);

  console.log(subUsers, productId);

  const fetchUsersList = useCallback(
    async (query = "") => {
      setLoading(true);
      try {
        const response = await fetchOrgUser({ page: 1, limit: 100, q: query });
        setSubUsers(response?.data?.subUsers ?? []);
      } catch (err) {
        console.error("Error fetching users:", err);
        setSubUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const fetchLicensesList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchLicenses();
      setLicensesList(response?.data?.availableLicenses ?? []);
    } catch (err) {
      console.error("Error fetching licenses:", err);
      setLicensesList([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const handleLicenseChange = (value: string) => {
    const selectedLicense = licensesList.find(
      (license) => license.product_id === value
    );
    console.log(selectedLicense, "line 128");

    if (selectedLicense) {
      const updatedSubscription: SubscriptionDetails = {
        name: selectedLicense.name,
        stripe_product_id: selectedLicense.product_id || "",
        quantity: selectedLicense.quantity,
        show_group_ids_dropdown: selectedLicense.show_group_ids_dropdown,
      };

      console.log(updatedSubscription, "line 141");

      setSelectedApps([]);
      setSelectedGroups({});
      setSelectedLicenseDetails(updatedSubscription);
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        subscriptionDetails: updatedSubscription,
      }));

      const productId = updatedSubscription?.stripe_product_id ?? "";
      setProductId(productId);
    }
  };

  const fetchGroupsList = useCallback(
    async (product_id: string) => {
      setLoading(true);
      try {
        const response = await fetchGroups({ product_id });
        const formattedGroups = response?.data?.groups.reduce(
          (acc: { [key: string]: Group[] }, app: AppGroups) => {
            acc[app.app_name] = app.groups;
            return acc;
          },
          {}
        );
        setAppGroups(formattedGroups || {});
      } catch (err) {
        console.error("Error fetching groups:", err);
      } finally {
        // setLoading(false);
      }
    },
    [setLoading]
  );

  const handleClose = () => {
    fetchUsersList();

    // setSelectedLicenseDetails(null);
    onClose();
  };

  useEffect(() => {
    if (isOpen && selectedUser && licensesList.length > 0) {
      const userLicense = licensesList.find(
        (license) =>
          license.product_id ===
          selectedUser.subscriptionDetails?.stripe_product_id
      );
      if (userLicense) {
        const updatedSubscription: SubscriptionDetails = {
          name: userLicense.name,
          stripe_product_id: userLicense.product_id,
          quantity: userLicense.quantity,
          show_group_ids_dropdown: userLicense.show_group_ids_dropdown,
        };
        setSelectedLicenseDetails(updatedSubscription);
        setProductid(userLicense.product_id);
      }
    }
  }, [selectedUser, licensesList]);

  const handleSave = async () => {
    console.log("Updated User Details:", userDetails);
    console.log("Selected License Details:", selectedLicenseDetails);
    if (!selectedUser?._id) {
      console.error("Error: selectedUser is missing an ID.");
      return; // Prevent function execution if no ID is found
    }
    // if (!selectedUser?.product_id) {
    //     selectedUser?.product_id=userDetails.subscriptionDetails.product_id
    // }
    if (!selectedUser.product_id) {
      selectedUser.product_id =
        userDetails.subscriptionDetails.stripe_product_id;
    }

    const data = {
      bmp_user_id: selectedUser?._id,
      updates: {
        name: userDetails.fullName,
        // email: userDetails.email,
        product_id: selectedLicenseDetails?.stripe_product_id,
        group_ids: Object.values(selectedGroups)
          .map((v) => (v ? +v : undefined))
          .filter((v) => !!v) as number[],
      },
    };
    console.log(data, "final data");
    await updateSubUser(data);
    await updateOnEdit();
    handleClose();
  };

  const updateSubUser = useCallback(
    async (data: {
      bmp_user_id: string;
      updates: {
        name: string;
        email?: string;
        product_id?: string;
        group_ids: Array<number>;
      };
    }) => {
      setLoading(true);
      try {
        console.log(data, "line 154");
        const response = await updateUser(data);
        console.log(response?.data, "fetch sub user");
        // s(false);

        await fetchUsersList();
        // setSubUsers(response?.data?.subUsers ?? []);
      } catch (err) {
        console.error("Error fetching users:", err);
        setSubUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );
  const toggleAppSelection = (appName: string) => {
    if (!selectedApps.includes(appName)) {
      setSelectedApps((prev) => [...prev, appName]);
      const group = appGroups[appName];
      setSelectedGroups((prev) => ({
        ...prev,
        [appName]: String(group[group.length - 1].group_id), // Ensure you access the correct property
      }));
    } else {
      setSelectedApps((prev) => prev.filter((a) => a !== appName));
      setSelectedGroups((prev) => ({
        ...prev,
        [appName]: undefined, // Ensure you access the correct property
      }));
    }
  };

  const toggleGroupSelection = (groupId: string, appName: string) => {
    setSelectedGroups((prev) => ({
      ...prev,
      [appName]: groupId, // Using dynamic key assignment for appName
    }));

    console.log("selected groups", selectedGroups, selectedApps);
  };

  useEffect(() => {
    console.log("line 319", productId);
    if (isOpen && productid) {
      fetchGroupsList(productid);
    }
  }, [isOpen, productid]);

  useEffect(() => {
    if (isOpen) {
      fetchLicensesList();
    }
  }, [isOpen]);

  return (
    <Dialog modal={false} open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl p-0 flex flex-col  rounded-xl overflow-hidden bg-white">
        <DialogHeader className="bg-[#f4f4f4] text-[#202b49] px-6 py-4 ">
          <DialogTitle className="text-xl font-semibold text-gray-700">
            Edit User Details
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 p-6 ">
          <Input
            placeholder="Contact Name"
            className="p-3 border rounded-md"
            value={userDetails.fullName}
            onChange={(e) =>
              setUserDetails({ ...userDetails, fullName: e.target.value })
            }
          />

          {/* <Input
                        placeholder="Enter Email"
                        className="p-3 border rounded-md"
                        value={userDetails.email}
                        onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                    /> */}

          {/* License Selection */}
          <Select
            value={selectedLicenseDetails?.stripe_product_id ?? ""}
            onValueChange={handleLicenseChange}
          >
            <SelectTrigger className="p-2 text-sm border rounded-md h-[40px] bg-white data-[placeholder]:text-[#828282] focus:ring-slate-300">
              <SelectValue placeholder="Select License" />
            </SelectTrigger>

            <SelectContent className="max-h-[200px] overflow-y-auto">
              {licensesList.map((license) => (
                <SelectItem
                  key={license.product_id}
                  value={license.product_id}
                  disabled={license.quantity === 0}
                >
                  {license.name} (Quantity: {license.quantity})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLicenseDetails?.show_group_ids_dropdown && (
            <Popover>
              <PopoverTrigger asChild>
                {/* <Button variant="outline">Open popover</Button> */}
                <div className="p-3 border flex justify-between items-center rounded-md bg-white cursor-pointer">
                  {selectedApps.length === 0 ? (
                    "Select Groups"
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {selectedApps.map((app) => {
                        const groupId = selectedGroups[app];
                        const group = appGroups[app]?.find(
                          (g) => String(g.group_id) === groupId
                        );
                        return (
                          <span
                            key={app}
                            className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-800"
                          >
                            {app}
                            {group ? ` (${group.group_name})` : ""}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 text-gray-500" />
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="w-80 z-[100]"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                tabIndex={0}
                asChild
              >
                <ScrollArea className="max-h-[200px] overflow-y-auto">
                  {Object.keys(appGroups).map((appName) => {
                    const appGroupList = appGroups[appName];
                    const lastGroup = appGroupList[appGroupList.length - 1]; // Get the last group

                    return (
                      <div
                        key={appName}
                        className="mb-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label className="flex cursor-pointer items-center space-x-2">
                          <Checkbox
                            checked={selectedApps.includes(appName)}
                            onCheckedChange={() => {
                              toggleAppSelection(appName);
                              if (
                                !selectedApps.includes(appName) &&
                                lastGroup
                              ) {
                                // Auto-select last group if selecting the app
                                toggleGroupSelection(
                                  String(lastGroup.group_id),
                                  appName
                                );
                              }
                            }}
                          />
                          <span className="font-semibold">{appName}</span>
                        </label>

                        {selectedApps.includes(appName) && (
                          <RadioGroup
                            name={appName + "-radio-group"}
                            value={selectedGroups[appName]} // Only one group can be selected
                            onValueChange={(value) =>
                              toggleGroupSelection(value, appName)
                            }
                          >
                            {appGroupList.map((group, ind) => (
                              <div
                                key={ind}
                                className="ml-4 flex items-center space-x-2"
                              >
                                <RadioGroupItem
                                  id={String(group.group_id)}
                                  value={String(group.group_id)}
                                />
                                <label
                                  htmlFor={String(group.group_id)}
                                  className="!cursor-pointer"
                                >
                                  {group.group_name}
                                </label>
                              </div>
                            ))}
                          </RadioGroup>
                        )}
                      </div>
                    );
                  })}
                  <ScrollBar />
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}
        </div>
        <div className="flex justify-end space-x-4 p-4">
          <Button variant="outline" onClick={handleClose} className=" h-[40px]">
            Cancel
          </Button>
          <Button
            className="bg-[#181818] text-white h-[40px]"
            onClick={handleSave}
          >
            Save & Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserDialog;
